// GPS-трек смены: пока приложение открыто, пишем позицию (когда курьер
// сдвинулся дальше 25 м и дальше погрешности фикса или прошла минута), копим
// в телефоне и отправляем пачками раз в 30 секунд. Без сети, при перегрузке
// или сбое сервера точки ждут — до 3000 штук — и уходят с нарастающей паузой.
//
// Каждая точка помнит свою смену: хвост прошлой смены, отправленный после
// старта новой, сервер положит в прошлую. Время телефона уходит вместе с
// пачкой (sentAt) — сервер по нему поправит сбитые часы.

import { useEffect } from 'react';
import { create } from 'zustand';
import type { AxiosError } from 'axios';

import { post } from '@/lib/api';
import { distanceMeters, onGeoPermissionChange } from '@/lib/geo';

/**
 * off — смены нет; searching — ждём первый фикс; ok — позиция свежая;
 * weak — спутников давно нет, но слежение идёт; denied — нет разрешения;
 * unavailable — телефон не отдаёт местоположение; unsupported — браузер не умеет.
 */
export type GpsStatus = 'off' | 'searching' | 'ok' | 'weak' | 'denied' | 'unavailable' | 'unsupported';

interface TrackState {
  status: GpsStatus;
  accuracy: number | null;
  /** Последняя позиция без фильтра — для «до точки N м» на экране. */
  position: { lat: number; lon: number; accuracy: number; at: number } | null;
  /** Сколько точек ждёт отправки. */
  pending: number;
}

const KEY = 'resstro-courier-track:v1';
const MAX_BUFFER = 3000;
const BATCH = 500;
const MIN_MOVE_M = 25;
const MIN_INTERVAL_MS = 60_000;
const FLUSH_MS = 30_000;
const FLUSH_AT_POINTS = 20;
/** Грубее — шум вышек, не шлём вовсе. 100–300 м сервер берёт только как «где сейчас». */
const MAX_ACCURACY_M = 300;
/** Фикс старше — уже не «GPS в порядке». */
const FIX_FRESH_MS = 100_000;
/**
 * Фикса нет дольше — перезапускаем слежение: iOS тихо усыпляет его в фоне, а
 * стоящему на месте курьеру браузер может не слать позицию — новый запуск
 * отдаёт её сразу, и руководитель видит, что курьер на связи.
 */
const WATCH_STALE_MS = 60_000;
/** Часы телефона перевели назад сильнее — не считаем точку повтором. */
const CLOCK_JUMP_MS = 5 * 60_000;
const RETRY_MIN_MS = 15_000;
const RETRY_MAX_MS = 5 * 60_000;

interface Pt {
  lat: number;
  lon: number;
  accuracy: number | null;
  speed: number | null;
  at: string;
  /** Смена, во время которой записана точка. У точек старых версий нет. */
  shiftId?: string;
}

function load(): Pt[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? (raw as Pt[]).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lon) && typeof p.at === 'string') : [];
  } catch {
    return [];
  }
}

let buffer: Pt[] = load();

export const useTrack = create<TrackState>(() => ({ status: 'off', accuracy: null, position: null, pending: buffer.length }));

function save() {
  if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);
  try {
    localStorage.setItem(KEY, JSON.stringify(buffer));
  } catch {
    /* переполнено — потеряем хвост трека, но не точки визитов */
  }
  useTrack.setState({ pending: buffer.length });
}

// ─── Отправка ───────────────────────────────────────────────────────────────

let inflight: Promise<void> | null = null;
let failures = 0;
let retryAt = 0;

/**
 * Повторить позже: нет сети, таймаут, сессия обновляется, сервер перегружен
 * (429) или упал (5xx). Остальные 4xx — сервер эту пачку не возьмёт никогда.
 */
export function isRetryableTrackError(err: unknown): boolean {
  const e = err as AxiosError;
  if (!e?.isAxiosError) return true;
  const status = e.response?.status;
  if (!status) return true;
  return status === 401 || status === 408 || status === 425 || status === 429 || status >= 500;
}

function backoff(n: number): number {
  const base = Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** Math.max(0, n - 1));
  return Math.round(base * (0.75 + Math.random() * 0.5));
}

async function send(force: boolean): Promise<void> {
  while (buffer.length > 0) {
    if (!force && Date.now() < retryAt) return;
    force = false;
    // Пачка — подряд идущие точки одной смены.
    const shiftId = buffer[0]!.shiftId;
    const batch: Pt[] = [];
    for (const p of buffer) {
      if (p.shiftId !== shiftId || batch.length >= BATCH) break;
      batch.push(p);
    }
    try {
      await post<{ accepted: number }>(
        '/track',
        {
          ...(shiftId ? { shiftId } : {}),
          sentAt: new Date().toISOString(),
          points: batch.map((p) => ({ lat: p.lat, lon: p.lon, accuracy: p.accuracy, speed: p.speed, at: p.at })),
        },
        { silent: true },
      );
      failures = 0;
      retryAt = 0;
    } catch (err) {
      if (isRetryableTrackError(err)) {
        failures += 1;
        retryAt = Date.now() + backoff(failures);
        return;
      }
      // Пачку отвергла проверка — не держим из-за неё всю очередь.
    }
    const sent = new Set(batch);
    buffer = buffer.filter((p) => !sent.has(p));
    save();
  }
}

/**
 * Отправить накопленный трек. Идущая отправка не дублируется — ждём её.
 * force — не ждать паузы после неудачной попытки (завершение смены).
 */
export function flushTrack(opts: { force?: boolean } = {}): Promise<void> {
  if (!inflight) {
    inflight = send(!!opts.force).finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

// ─── Слежение ───────────────────────────────────────────────────────────────

let currentShift: string | null = null;
let watchId: number | null = null;
let last: Pt | null = null;
let lastFixAt = 0;
let watchStartedAt = 0;

/** Писать ли фикс в трек: сдвинулся дальше погрешности или прошла минута. */
function keep(p: Pt): boolean {
  if (!last || last.shiftId !== p.shiftId) return true;
  const dt = Date.parse(p.at) - Date.parse(last.at);
  // Тот же фикс из кэша браузера (перезапуск слежения) — не нужен; часы перевели назад — пишем.
  if (dt <= 0) return dt < -CLOCK_JUMP_MS;
  const moved = distanceMeters(last, p) > Math.max(MIN_MOVE_M, p.accuracy ?? 0);
  return moved || dt >= MIN_INTERVAL_MS;
}

function onFix(pos: GeolocationPosition) {
  lastFixAt = Date.now();
  const accuracy = Math.round(pos.coords.accuracy);
  useTrack.setState({
    status: 'ok',
    accuracy,
    position: { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy, at: pos.timestamp },
  });
  if (!currentShift || !Number.isFinite(accuracy) || accuracy > MAX_ACCURACY_M) return;
  const speed = pos.coords.speed;
  const p: Pt = {
    lat: pos.coords.latitude,
    lon: pos.coords.longitude,
    accuracy,
    speed: speed != null && speed >= 0 && speed <= 500 ? Math.round(speed * 10) / 10 : null,
    at: new Date(pos.timestamp).toISOString(),
    shiftId: currentShift,
  };
  if (!keep(p)) return;
  last = p;
  buffer.push(p);
  save();
  if (buffer.length >= FLUSH_AT_POINTS) void flushTrack();
}

function onError(err: GeolocationPositionError) {
  if (err.code === err.PERMISSION_DENIED) {
    // После отказа браузер слежение не возобновит — перезапустим, когда разрешат.
    stopWatch();
    useTrack.setState({ status: 'denied' });
    return;
  }
  // Таймаут и «позиции нет» — слежение продолжается; свежий фикс есть — статус не портим.
  if (Date.now() - lastFixAt < FIX_FRESH_MS) return;
  useTrack.setState({ status: err.code === err.TIMEOUT ? 'weak' : 'unavailable' });
}

function stopWatch() {
  if (watchId != null && 'geolocation' in navigator) navigator.geolocation.clearWatch(watchId);
  watchId = null;
}

function startWatch() {
  stopWatch();
  if (!currentShift) return;
  if (!('geolocation' in navigator)) {
    useTrack.setState({ status: 'unsupported' });
    return;
  }
  const { status } = useTrack.getState();
  if (status !== 'ok' && status !== 'weak') useTrack.setState({ status: 'searching' });
  watchStartedAt = Date.now();
  watchId = navigator.geolocation.watchPosition(onFix, onError, { enableHighAccuracy: true, maximumAge: 15_000, timeout: 60_000 });
}

/** Курьер нажал «Проверить снова» или вернулся из настроек — запросить GPS заново. */
export function retryGps(): void {
  if (currentShift) startWatch();
}

/** Раз в 30 секунд: честный статус и перезапуск уснувшего слежения. */
function checkWatch() {
  if (!currentShift) return;
  const now = Date.now();
  const { status } = useTrack.getState();
  if (status === 'ok' && now - lastFixAt > FIX_FRESH_MS) useTrack.setState({ status: 'weak' });
  if (status === 'denied' || status === 'unsupported') return;
  // Первого фикса ждём дольше: курьер, может быть, ещё отвечает на запрос разрешения.
  const staleAfter = lastFixAt === 0 ? 3 * WATCH_STALE_MS : WATCH_STALE_MS;
  if (now - Math.max(lastFixAt, watchStartedAt) > staleAfter) startWatch();
}

/** Писать трек смены shiftId, пока приложение открыто. null — смены нет. */
export function useTrackRecorder(shiftId: string | null): void {
  // Хвост трека уходит и после смены: таймер, возврат в приложение, появление сети.
  useEffect(() => {
    const timer = window.setInterval(() => {
      checkWatch();
      if (buffer.length > 0) void flushTrack();
    }, FLUSH_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Из фона (или из настроек с разрешением) — слежение могло уснуть. Пока
      // ждём первый фикс (а может, курьер отвечает на запрос разрешения) — не трогаем.
      const { status } = useTrack.getState();
      const now = Date.now();
      const stale =
        status === 'searching' ? now - watchStartedAt > 60_000 : status === 'ok' ? now - lastFixAt > 30_000 : status !== 'off' && status !== 'unsupported';
      if (currentShift && stale) startWatch();
      void flushTrack();
    };
    const onOnline = () => {
      retryAt = 0;
      void flushTrack();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    void flushTrack();
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  useEffect(() => {
    if (!shiftId) {
      currentShift = null;
      stopWatch();
      useTrack.setState({ status: 'off', accuracy: null, position: null });
      return;
    }
    currentShift = shiftId;
    startWatch();
    // Разрешили в настройках — сразу снова слушаем GPS; запретили — честно показываем.
    const offPermission = onGeoPermissionChange((state) => {
      if (state === 'denied') {
        stopWatch();
        useTrack.setState({ status: 'denied' });
      } else {
        const { status } = useTrack.getState();
        if (watchId == null || status === 'denied' || status === 'unavailable') startWatch();
      }
    });
    return () => {
      offPermission();
      stopWatch();
      currentShift = null;
      void flushTrack();
    };
  }, [shiftId]);
}
